<template>
  <Modal v-if="modals.role && availableRoles.length" @close="close">
    <h3>
      {{ $t("modals.role.chooseCharacterFor") }}
      {{
        playerIndex >= 0 && players.length
          ? players[playerIndex].name
          : $t("modals.role.bluffing")
      }}
    </h3>
    
    <!-- Show all roles checkbox (only for bluff selection) -->
    <div v-if="playerIndex < 0" class="bluff-options">
      <label class="checkbox-label">
        <input 
          type="checkbox" 
          v-model="showAllRoles"
          class="checkbox-input"
        />
        <span>{{ $t("modals.role.showAllRoles") }}</span>
      </label>
    </div>
    <ul class="tokens" v-if="tab === 'editionRoles' || !otherTravelers.size">
      <li
        v-for="role in availableRoles"
        :class="[role.team, { 'show-ability': showingAbilityFor === role.id }]"
        :key="role.id"
        @click="setRole(role)"
      >
        <Token :role="role" />
      </li>
    </ul>
    <ul class="tokens" v-if="tab === 'otherTravelers' && otherTravelers.size">
      <li
        v-for="role in otherTravelers.values()"
        :class="[role.team, { 'show-ability': showingAbilityFor === role.id }]"
        :key="role.id"
        @click="setRole(role)"
      >
        <Token :role="role" />
      </li>
    </ul>
    <div
      class="button-group"
      v-if="playerIndex >= 0 && otherTravelers.size && !session.isSpectator"
    >
      <span
        class="button"
        :class="{ townsfolk: tab === 'editionRoles' }"
        @click="tab = 'editionRoles'"
        >{{ $t("modals.role.editionRoles") }}</span
      >
      <span
        class="button"
        :class="{ townsfolk: tab === 'otherTravelers' }"
        @click="tab = 'otherTravelers'"
        >{{ $t("modals.role.otherTravelers") }}</span
      >
    </div>
  </Modal>
</template>

<script>
import { mapMutations, mapState } from "vuex";
import Modal from "./Modal";
import Token from "../Token";

export default {
  components: { Token, Modal },
  props: ["playerIndex"],
  computed: {
    availableRoles() {
      const availableRoles = [];
      const players = this.$store.state.players.players;
      this.$store.state.roles.forEach((role) => {
        // For regular player role selection (playerIndex >= 0)
        if (this.playerIndex >= 0) {
          availableRoles.push(role);
        } 
        // For bluff selection (playerIndex < 0)
        else {
          // If "show all roles" is enabled, show all roles
          if (this.showAllRoles) {
            availableRoles.push(role);
          } 
          // Otherwise, only show roles not assigned to players (default behavior)
          else if (!players.some((player) => player.role.id === role.id)) {
            availableRoles.push(role);
          }
        }
      });
      availableRoles.push({});
      return availableRoles;
    },
    ...mapState(["modals", "roles", "session"]),
    ...mapState("players", ["players"]),
    ...mapState(["otherTravelers"]),
  },
  data() {
    return {
      tab: "editionRoles",
      showingAbilityFor: null, // Track which role is showing ability on mobile
      showAllRoles: false, // Show all roles option for bluff selection
    };
  },
  methods: {
    setRole(role) {
      // On touch devices (no hover capability), first tap shows ability, second tap selects
      const isTouchDevice = window.matchMedia('(hover: none)').matches;
      if (isTouchDevice && role.id) {
        if (this.showingAbilityFor === role.id) {
          // Second tap - actually select the role
          this.showingAbilityFor = null;
        } else {
          // First tap - just show the ability
          this.showingAbilityFor = role.id;
          return;
        }
      }
      
      if (this.playerIndex < 0) {
        // assign to bluff slot (index < 0)
        this.$store.commit("players/setBluff", {
          index: this.playerIndex * -1 - 1,
          role,
        });
      } else {
        if (this.session.isSpectator && role.team === "traveler") return;
        // assign to player
        const player = this.$store.state.players.players[this.playerIndex];
        this.$store.commit("players/update", {
          player,
          property: "role",
          value: role,
        });
      }
      this.tab = "editionRoles";
      this.$store.commit("toggleModal", "role");
    },
    close() {
      this.tab = "editionRoles";
      this.toggleModal("role");
    },
    ...mapMutations(["toggleModal"]),
  },
};
</script>

<style scoped lang="scss">
@import "../../vars.scss";

ul.tokens li {
  border-radius: 50%;
  width: 9vw; // Increased from 6vw for better visibility
  margin: 1%;
  transition: transform 500ms ease;

  &.townsfolk {
    box-shadow:
      0 0 10px $townsfolk,
      0 0 10px #004cff;
  }
  &.outsider {
    box-shadow:
      0 0 10px $outsider,
      0 0 10px $outsider;
  }
  &.minion {
    box-shadow:
      0 0 10px $minion,
      0 0 10px $minion;
  }
  &.demon {
    box-shadow:
      0 0 10px $demon,
      0 0 10px $demon;
  }
  &.traveler {
    box-shadow:
      0 0 10px $traveler,
      0 0 10px $traveler;
  }
  &:hover {
    transform: scale(1.2);
    z-index: 10;
  }
}

#townsquare.spectator ul.tokens li.traveler {
  display: none;
}

// Show ability tooltip on touch devices when tapped once
@media (hover: none) {
  ul.tokens li.show-ability .token .ability {
    opacity: 1 !important;
  }
}

// Bluff options checkbox styling
.bluff-options {
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 10px 0 15px 0;
  padding: 10px 15px;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 8px;
  
  .checkbox-label {
    display: flex;
    align-items: center;
    gap: 10px;
    cursor: pointer;
    user-select: none;
    
    .checkbox-input {
      width: 18px;
      height: 18px;
      cursor: pointer;
      accent-color: #4a90e2;
    }
    
    span {
      font-size: 14px;
      color: rgba(255, 255, 255, 0.9);
    }
    
    &:hover span {
      color: white;
    }
  }
}
</style>
